import { useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';
import { useMagicPenStore } from '@src/stores';

import { cancelAllTtlTimeouts, destroyTextEditorsByIds } from '@src/components/MagicPen/helpers/canvas.helpers';

import { EMagicPenActionType, EMagicPenSockets, ICanvasObject } from '@src/types';

/**
 * Hook for handling Magic Pen socket events
 * Listens for incoming annotations from other peers and adds them to the store
 */
const useMagicPenSocketListeners = ({ isConnected }: { isConnected: boolean }) => {
  const { addObjects } = useMagicPenStore(
    useShallow((state) => ({
      addObjects: state.addObjects,
    }))
  );

  useEffect(() => {
    if (!isConnected) return;

    const handleMagicPenActions = (data: {
      actionType: EMagicPenActionType;
      annotations: ICanvasObject[];
      annotationsIds?: string[];
      from_peer_id?: string;
      from_peer_name?: string;
    }) => {
      try {
        switch (data.actionType) {
          case EMagicPenActionType.ADD_ANNOTATE: {
            const annotations = data?.annotations;
            if (!annotations) {
              return;
            }

            addObjects(annotations);
            break;
          }
          case EMagicPenActionType.UPDATE_ANNOTATE: {
            const annotations = data?.annotations;
            if (!annotations) {
              return;
            }

            useMagicPenStore.setState((state) => {
              let hasChanges = false;

              // Index incoming annotations by id for O(1) lookups while iterating existing objects
              const incomingMap = new Map<string, ICanvasObject>();
              annotations.forEach((annotation) => incomingMap.set(annotation.id, annotation));

              let updatedObjects = state.objects.map((obj) => {
                const incomingAnnotation = incomingMap.get(obj.id);
                if (!incomingAnnotation) {
                  return obj;
                }

                // Merge the existing object with new data and track whether any field values actually changed
                const merged = { ...obj, ...incomingAnnotation };
                // Check whether any of the fields in incomingAnnotation are actually different from obj:
                const changed = (Object.keys(incomingAnnotation) as (keyof ICanvasObject)[]).some(
                  (key) => obj[key] !== incomingAnnotation[key]
                );
                if (changed) {
                  hasChanges = true;
                  return merged;
                }
                return obj;
              });

              incomingMap.forEach((annotation, id) => {
                const exists = state.objects.some((obj) => obj.id === id);
                if (!exists) {
                  // If the incoming annotation is new, append it to the store
                  hasChanges = true;
                  updatedObjects = [...updatedObjects, annotation];
                }
              });

              return hasChanges ? { objects: updatedObjects } : state;
            });

            break;
          }
          case EMagicPenActionType.DELETE_ANNOTATION: {
            const annotationsIds = data?.annotationsIds;
            if (!annotationsIds || !Array.isArray(annotationsIds)) return;

            // Cancel all pending TTL deletion timeouts when annotations are deleted
            cancelAllTtlTimeouts();

            useMagicPenStore.setState((state) => ({
              objects: state.objects.filter((obj) => !annotationsIds.includes(obj.id)),
            }));

            destroyTextEditorsByIds(annotationsIds);

            break;
          }

          default:
            console.warn('Unknown magic pen action type:', data.actionType);
            break;
        }
      } catch (error) {
        console.error('Error handling magic pen socket event:', { error });
      }
    };

    SocketService.on(EMagicPenSockets.MAGIC_PEN, handleMagicPenActions);

    return () => {
      SocketService.off(EMagicPenSockets.MAGIC_PEN, handleMagicPenActions);
    };
  }, [isConnected, addObjects]);
};

export default useMagicPenSocketListeners;
